import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { toPublicLabelUrl } from '../../utils/s3Utils';

export const fetchDeliveryItems = createAsyncThunk(
  'deliveryItems/fetchDeliveryItems',
  async (orderId, { getState, rejectWithValue }) => {
    try {
      const { deliveryItems } = getState();
      const { filters, pagination } = deliveryItems;
      const params = {
        search: filters.search || undefined,
        page: pagination.page,
        page_size: pagination.rowsPerPage,
      };
      const response = await api.get(`deliveries/${orderId}/delivery_items/list/`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar los bultos'
      );
    }
  }
);

/**
 * Every label of the order, ignoring the table's pagination.
 *
 * Printing has to cover the whole order: the table may be showing page 2 of 5,
 * and merging only the visible rows would leave parcels without a label.
 *
 * @param {number|string} orderId
 * @returns {Promise<string[]>} URLs of the labels already generated, pointed at
 *   the bucket the way the deliveries list points them.
 */
export const fetchAllDeliveryItemLabels = createAsyncThunk(
  'deliveryItems/fetchAllDeliveryItemLabels',
  async (orderId, { getState, rejectWithValue }) => {
    try {
      const { count, data } = getState().deliveryItems;

      // The current page already holds every item; no need to ask again.
      const items =
        count > 0 && data.length === count
          ? data
          : (
              await api.get(`deliveries/${orderId}/delivery_items/list/`, {
                params: { page: 1, page_size: Math.max(count, 1) },
              })
            ).data.results || [];

      return items
        .map((item) => (item.label ? toPublicLabelUrl(item.label) : null))
        .filter(Boolean);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al obtener las etiquetas de la orden'
      );
    }
  }
);

export const createDeliveryItem = createAsyncThunk(
  'deliveryItems/createDeliveryItem',
  async ({ orderId, item }, { dispatch, rejectWithValue }) => {
    try {
      await api.post(`deliveries/${orderId}/delivery_items/create/`, item);
      // Awaited, so the action is still running until the table shows its result.
      await dispatch(fetchDeliveryItems(orderId));
      return true;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear el bulto'
      );
    }
  }
);

export const deleteDeliveryItem = createAsyncThunk(
  'deliveryItems/deleteDeliveryItem',
  async ({ itemId, orderId }, { dispatch, rejectWithValue }) => {
    try {
      await api.delete(`deliveries/delivery_items/${itemId}/delete/`);
      // Until the table comes back without it, the row is still there to click.
      await dispatch(fetchDeliveryItems(orderId));
      return itemId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar el bulto'
      );
    }
  }
);

/**
 * Sets the total number of bultos in one call.
 *
 * The backend adds or removes from the end of the list and re-indexes what is
 * left, so this is not the same as calling create or delete N times: it returns
 * the whole rebuilt list, which replaces what the table was showing.
 *
 * @param {{orderId: number|string, count: number}} args
 */
export const setDeliveryItemsCount = createAsyncThunk(
  'deliveryItems/setDeliveryItemsCount',
  async ({ orderId, count }, { rejectWithValue }) => {
    try {
      const response = await api.post(`deliveries/${orderId}/delivery_items/set_count/`, { count });
      return {
        items: response.data.items || [],
        count: response.data.count ?? count,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al establecer la cantidad de bultos'
      );
    }
  }
);

/**
 * Saves the weight, the dimensions and the products of one bulto.
 *
 * Once every bulto has all four measurements the delivery's own weight is
 * rewritten with their sum, which is what the courier is quoted on — otherwise
 * the parcel would travel with whatever weight was typed when the order was
 * created.
 *
 * @param {{itemId: number|string, deliveryId: number|string, values: object}} args
 */
export const updateDeliveryItemLogistics = createAsyncThunk(
  'deliveryItems/updateDeliveryItemLogistics',
  async ({ itemId, deliveryId, values }, { getState, rejectWithValue }) => {
    try {
      const response = await api.patch(
        `deliveries/delivery_items/${itemId}/update_logistics/`,
        values
      );
      const updatedItem = response.data;

      const items = getState().deliveryItems.data.map((item) =>
        item.id === itemId ? updatedItem : item
      );

      const complete = items.every(
        (item) =>
          item.weight_grams > 0 &&
          item.height_cm > 0 &&
          item.width_cm > 0 &&
          item.length_cm > 0
      );

      if (complete && deliveryId) {
        const weight = items.reduce((sum, item) => sum + (item.weight_grams || 0), 0);
        await api.patch(`deliveries/${deliveryId}/update_weight/`, { weight });
      }

      return updatedItem;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al guardar las variables logísticas'
      );
    }
  }
);

export const updateDeliveryItemStatus = createAsyncThunk(
  'deliveryItems/updateDeliveryItemStatus',
  async ({ itemId, status, orderId }, { dispatch, rejectWithValue }) => {
    try {
      await api.patch(`deliveries/delivery_items/${itemId}/update_status/`, { status });
      // Awaited, so the row's pencil spins until the table shows the new status.
      await dispatch(fetchDeliveryItems(orderId));
      return { itemId, status };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar estado del bulto'
      );
    }
  },
  // Keyed by the bulto, so only its row shows the change on its way.
  { getPendingMeta: ({ arg }) => ({ pendingKey: arg.itemId }) }
);
